import { afterNextRender, ChangeDetectionStrategy, Component, computed, contentChildren, DestroyRef, effect, ElementRef, inject, linkedSignal, OnInit, PLATFORM_ID, Renderer2, signal, untracked } from '@angular/core'
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { map, Observable, throttleTime } from 'rxjs'

import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltipModule } from '@angular/material/tooltip'

import { type OverflowMenuItem } from './responsive-overflow-menu-source'
import { ResponsiveContentElement } from './responsive-content-element'
import { ResponsiveUi } from '../responsive-ui'

export interface ElementMeasurement {
	element: ResponsiveContentElement
	width: number
}

type ElementWithOverflowFlag = {
	element: ResponsiveContentElement
	overflow: boolean
}

interface MeasurementTriggers {
	containerWidth: number
	contentElements: ResponsiveContentElement[]
}

interface OverflowMenuGroup {
	label: string
	icon: string | null
	items: readonly OverflowMenuItem[]
}

/** Manages a row of responsive content elements.
 * Elements that don't fit in the available width are hidden and added to an overflow menu,
 * while `flex` elements shrink to fit and are never added to the overflow menu. Pinned
 * buttons are also never added to the overflow menu.
 *
 * Projected `menu` components must keep a real box on `:host` (e.g. `display: block`)
 * so host-based measurement and visibility toggling remain correct. */
@Component({
	selector: 'app-responsive-content',
	imports: [MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule, NgTemplateOutlet],
	templateUrl: './responsive-content.html',
	styleUrl: './responsive-content.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResponsiveContent implements OnInit {
	private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef)
	private readonly destroyRef = inject(DestroyRef)
	private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID))
	private readonly renderer = inject(Renderer2)
	private readonly responsiveUi = inject(ResponsiveUi)

	// when these 2 signals change, the widths of the child elements are re-measured and element overflow is recalculated
	private readonly contentElements = contentChildren(ResponsiveContentElement)
	private readonly containerWidth = signal(0)

	/** Child elements with their widths. */
	private readonly childrenMeasurements = linkedSignal<MeasurementTriggers, ElementMeasurement[]>({
		source: () => ({
			containerWidth: this.containerWidth(),
			contentElements: Array.from(this.contentElements()),
		}),
		computation: ({ contentElements }, previousMeasurements) => {
			if (!this.isBrowser) return []
			return this.measureElementWidths(contentElements, previousMeasurements?.value ?? [])
		},
	})

	/** Total width of all child elements including flex column gaps. `new Number()` is used to trigger downstream
	 * reactive updates when the newly calculated total is the same as the previous total, because the total width
	 * remains the same when the container width changes, yet we still want to recalculate which elements overflow in that case. */
	private readonly totalChildrenWidth = computed(() => new Number(this.totalMeasuredWidth(this.childrenMeasurements())))

	/** Child elements with their overflow flags. */
	private readonly elementsWithOverflowFlags = computed(() => this.flagOverflowingElements(this.totalChildrenWidth().valueOf()))

	/** Child elements that overflow the container and are therefore represented in the overflow menu. */
	protected readonly overflowElements = computed(() => this.elementsWithOverflowFlags().filter(({ overflow }) => overflow).map(({ element }) => element))

	/** Flat overflow items from direct button hosts and single-item menu sources. */
	protected readonly overflowFlatItems = computed(() => {
		const fromButtons = this.overflowElements()
			.filter(element => element.isButtonHost())
			.flatMap(element => element.getOverflowItems())

		const fromSingleItemMenus = this.overflowElements()
			.filter(element => !element.isButtonHost() && element.getOverflowItems().length === 1)
			.flatMap(element => element.getOverflowItems())

		return [...fromButtons, ...fromSingleItemMenus]
	})

	/** Grouped overflow items from wrapper hosts with multiple items (menu role with OVERFLOW_MENU_SOURCE). */
	protected readonly overflowWrapperGroups = computed(() =>
		this.overflowElements()
			.filter(element => !element.isButtonHost() && element.getOverflowItems().length > 1)
			.map<OverflowMenuGroup>(element => ({
				label: element.getOverflowGroupLabel(),
				icon: element.getOverflowGroupIcon(),
				items: element.getOverflowItems(),
			})),
	)

	protected readonly showOverflowMenu = computed(() =>
		this.overflowFlatItems().length > 0 || this.overflowWrapperGroups().length > 0,
	)

	/** Flex column gap between child elements. */
	private columnGap = 0
	/** Width of the button that opens the overflow menu. */
	private readonly OVERFLOW_BUTTON_WIDTH = 40

	constructor() {
		// observe container width resizing to trigger measurements and overflow calculations
		afterNextRender(() => this.observeContainerWidth().subscribe(width => this.containerWidth.set(width)))

		// sync host's flex-basis to child elements' measured total width so the flex parent can shrink the host without feedback loops
		effect(() => this.renderer.setStyle(this.hostRef.nativeElement, 'flex-basis', `${this.totalChildrenWidth()}px`))

		// show/hide buttons whenever the element overflow flags change
		effect(() => this.updateElementsVisibility(this.elementsWithOverflowFlags()))
	}

	ngOnInit(): void {
		if (!this.isBrowser) return
		// retrieve host's flex column gap for overflow calculations
		this.columnGap = parseFloat(getComputedStyle(this.hostRef.nativeElement).columnGap) || 0
	}

	/** Watch the host element for size changes. */
	private observeContainerWidth(): Observable<number> {
		return this.responsiveUi.observeResize(this.hostRef.nativeElement).pipe(
			throttleTime(75, undefined, { trailing: true }),
			map(entries => entries[0].contentRect.width),
			takeUntilDestroyed(this.destroyRef),
		)
	}

	/** Measure each element's natural width.
	 * DOM manipulation occurs to take measurements, then DOM is returned to its original state. */
	private measureElementWidths(elements: ResponsiveContentElement[], existingMeasurements: ElementMeasurement[]): ElementMeasurement[] {
		const knownWidths = new Map(
			existingMeasurements.map(({ element, width }) => [element, width]),
		)

		return elements.map(element => {
			const nativeElement = element.hostRef.nativeElement

			// hidden elements
			if (nativeElement.style.display === 'none') {
				// use cached width if available
				const knownWidth = knownWidths.get(element)
				if (knownWidth) return { element, width: knownWidth }

				// temporarily show element, measure, and hide again
				this.renderer.removeStyle(nativeElement, 'display')
				const width = nativeElement.getBoundingClientRect().width
				this.renderer.setStyle(nativeElement, 'display', 'none')
				return { element, width }
			}

			// visible shrinkable elements: temporarily prevent shrinking to measure natural width
			if (getComputedStyle(nativeElement).flexShrink !== '0') {
				const savedFlexShrink = nativeElement.style.flexShrink
				this.renderer.setStyle(nativeElement, 'flex-shrink', '0')
				const width = nativeElement.getBoundingClientRect().width
				this.renderer.setStyle(nativeElement, 'flex-shrink', savedFlexShrink)
				return { element, width }
			}

			// visible non-shrinkable elements: use cache or measure
			const knownWidth = knownWidths.get(element)
			if (knownWidth) return { element, width: knownWidth }

			return { element, width: nativeElement.getBoundingClientRect().width }
		})
	}

	/** Calculate the total width of all measured elements, including the gaps between them. */
	private totalMeasuredWidth(elements: ElementMeasurement[]): number {
		const totalElementsWidth = elements.reduce((sum, { width }) => sum + width, 0)
		const totalGapsWidth = Math.max(0, elements.length - 1) * this.columnGap
		return totalElementsWidth + totalGapsWidth
	}

	/** Determine which children overflow their container.
	 * `flex` elements shrink into remaining space down to their `min-width` and are never considered overflowing.
	 * `pinned` buttons are also never considered overflowing. */
	private flagOverflowingElements(totalElementsWidth: number): ElementWithOverflowFlag[] {
		const measurements = untracked(() => this.childrenMeasurements())
		if (!measurements.length) return []

		// if everything fits, mark all elements as visible and skip the rest of the logic
		const containerWidth = this.hostRef.nativeElement.getBoundingClientRect().width
		if (totalElementsWidth <= containerWidth) return measurements.map(({ element }) => ({ element, overflow: false }))

		const overflowFlags: ElementWithOverflowFlag[] = []
		let usedWidth = 0
		let elementsThatFitCount = 0
		let overflowing = false

		for (const { element, width } of measurements) {
			const gap = elementsThatFitCount > 0 ? this.columnGap : 0

			// pinned buttons are never considered overflowing
			if (element.isPinnedButton()) {
				usedWidth += width + gap
				overflowFlags.push({ element, overflow: false })
				elementsThatFitCount++
				continue
			}

			// elements without overflow menu items are not candidates for the overflow menu and will never be hidden
			if (!element.isOverflowCandidate()) {
				const minWidth = parseFloat(getComputedStyle(element.hostRef.nativeElement).minWidth) || 0
				usedWidth += minWidth + gap
				overflowFlags.push({ element, overflow: false })
				elementsThatFitCount++
				continue
			}

			if (overflowing) {
				overflowFlags.push({ element, overflow: true })
				continue
			}

			if (usedWidth + gap + width + gap + this.OVERFLOW_BUTTON_WIDTH <= containerWidth) {
				usedWidth += width + gap
				elementsThatFitCount++
				overflowFlags.push({ element, overflow: false })
			} else {
				overflowing = true
				overflowFlags.push({ element, overflow: true })
			}
		}
		return overflowFlags
	}

	/** Using the given overflow flags, adjust the visibility of child elements. */
	private updateElementsVisibility(elementsWithOverflowFlags: ElementWithOverflowFlag[]): void {
		elementsWithOverflowFlags.forEach(({ element, overflow }) => {
			if (overflow) this.renderer.setStyle(element.hostRef.nativeElement, 'display', 'none')
			else this.renderer.removeStyle(element.hostRef.nativeElement, 'display')
		})
	}

}
